import { useState } from 'react'
import { Button, Cell } from '@highlight/ui'
import { Toast } from '../index'
import { toastManager } from '../toast-store'
import { autoResolutionOf, type ReminderApiItem } from '@renderer/overlay/Sidepanel/hooks/reminder-mapping'
import { useReminderActions } from '@renderer/overlay/Sidepanel/hooks/use-reminders'
import { useOpenViewInSidepanel } from '@renderer/overlay/hooks/use-open-view-in-sidepanel'

/**
 * Shows what Highlight saw when it checked a reminder off, so the user can
 * judge the call. After an Undo the server never auto-resolves it again.
 */
export function AutoResolvedReminderToast({ reminder, toastId }: { reminder: ReminderApiItem; toastId: string }) {
  const { setResolved } = useReminderActions()
  const { openViewInSidepanel } = useOpenViewInSidepanel()
  const [reopened, setReopened] = useState(false)
  const evidence = autoResolutionOf(reminder)?.evidence

  const handleUndo = () => {
    setResolved(reminder.id, false, { trigger: 'auto-resolve-undo' })
    setReopened(true)
    setTimeout(() => toastManager.dismiss(toastId), 1200)
  }

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        <Cell
          clickable
          onClick={() => {
            openViewInSidepanel('reminders', {
              initialTab: reopened ? 'active' : 'resolved',
              focusReminderId: reminder.id,
            })
            toastManager.dismiss(toastId)
          }}
          className="border-0 p-0 opacity-100 shadow-none hover:border-0"
        >
          <Cell.Header showChevron={false}>
            <Cell.Title
              className={
                reopened
                  ? 'text-foreground line-clamp-2 text-sm'
                  : 'text-foreground-secondary decoration-foreground-placeholder line-clamp-2 text-sm line-through'
              }
            >
              {reminder.text}
            </Cell.Title>
          </Cell.Header>
          {evidence && (
            <Cell.Footer className="text-xs">
              <span className="text-foreground-secondary line-clamp-2">
                {reopened ? 'Reopened. Highlight won’t check this one off again.' : `Seen on your screen: ${evidence}`}
              </span>
            </Cell.Footer>
          )}
        </Cell>
        {!reopened && (
          <div className="mt-3 flex justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation()
                handleUndo()
              }}
            >
              Undo
            </Button>
          </div>
        )}
      </Toast.Content>
    </Toast>
  )
}
